import * as React from "react";
import { cn } from "@/lib/utils";
import { SparklineSkeleton } from "./sparkline";

export interface KPIStripProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const KPIStrip = React.forwardRef<HTMLDivElement, KPIStripProps>(
  ({ className, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        "grid grid-cols-5 overflow-hidden rounded-[14px] border border-border-subtle bg-bg-surface",
        className
      )}
      {...rest}
    />
  )
);
KPIStrip.displayName = "KPIStrip";

export interface KPIStripItemProps extends React.HTMLAttributes<HTMLDivElement> {
  accent?: boolean;
}

export const KPIStripItem = React.forwardRef<HTMLDivElement, KPIStripItemProps>(
  ({ accent = false, className, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        "border-r border-border-subtle px-5 py-[18px] last:border-r-0",
        "flex min-w-0 flex-col gap-2",
        accent && "bg-gradient-to-b from-brand-50 to-transparent",
        className
      )}
      {...rest}
    />
  )
);
KPIStripItem.displayName = "KPIStripItem";

export function KPIStripItemLabel({
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
  return (
    <div
      className={cn(
        "text-[11px] font-medium uppercase tracking-[0.06em] text-text-secondary",
        className
      )}
      {...rest}
    />
  );
}

export function KPIStripItemValue({
  className,
  ...rest
}: React.HTMLAttributes<HTMLSpanElement>): React.ReactElement {
  return (
    <span
      className={cn(
        "font-mono text-[30px] font-medium tabular-nums leading-[1.1]",
        "tracking-[-0.02em] text-text-primary",
        className
      )}
      {...rest}
    />
  );
}

export function KPIStripSkeleton({
  items = 5,
  className,
}: {
  items?: number;
  className?: string;
}): React.ReactElement {
  return (
    <KPIStrip className={className} aria-hidden="true">
      {Array.from({ length: items }).map((_, i) => (
        <KPIStripItem key={i}>
          <div className="bg-bg-muted h-2 w-20 rounded motion-safe:animate-pulse-dot" />
          <div className="bg-bg-muted mt-1 h-7 w-16 rounded motion-safe:animate-pulse-dot" />
          <SparklineSkeleton w={60} h={14} bars={8} />
        </KPIStripItem>
      ))}
    </KPIStrip>
  );
}
